import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import IcAllowance from '@deliveryhero/pd-cookbook/components/Icons/IcAllowance';
import IcDiagonal from '@deliveryhero/pd-cookbook/components/Icons/IcDiagonal';
import IcGift from '@deliveryhero/pd-cookbook/components/Icons/IcGift';
import IcNoFee from '@deliveryhero/pd-cookbook/components/Icons/IcNoFee';
import IcOrders from '@deliveryhero/pd-cookbook/components/Icons/IcOrders';
import IcPhoneInactive from '@deliveryhero/pd-cookbook/components/Icons/IcPhoneInactive';
import IcPro from '@deliveryhero/pd-cookbook/components/Icons/IcPro';
import IcProfile from '@deliveryhero/pd-cookbook/components/Icons/IcProfile';
import IcVipSupport from '@deliveryhero/pd-cookbook/components/Icons/IcVipSupport';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import { Card } from 'components/Card';
import { UpgradePlanForm } from 'components/Forms/UpgradePlan';
import Page, { ActionType } from 'components/Page';
import FoodpandaForBusiness from 'components/svg/FoodpandaForBusiness';
import PauPauGift from 'components/svg/PauPauGift';
import { useConfiguration } from 'contexts/country-config';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import useUser from 'hooks/useUser';
import {
  SalesforceTag,
  SuccessResponse,
  UpgradePlanPayload
} from 'services/api';
import { phoneCountryCodes } from 'share/phone-country-codes';
import { AppRoute } from 'share/types/routes';

import { LabelText } from './constants';
import {
  DropdownOptions,
  EmployeeRangeOptions,
  IndustryKey,
  ReasonKey,
  UpgradePlanAction
} from './types';

import './styles.scss';

const DEFAULT_OPTION = 'default';

export function UpgradePlanContainer() {
  const api = useApi();
  const config = useConfiguration();
  const countryCode = config.code.toLowerCase();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useUser();
  const [, setAction] = useAction<UpgradePlanAction>();
  const [cities, setCities] = useState<string[]>(['City']);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    industries: [DEFAULT_OPTION],
    reasons: [DEFAULT_OPTION]
  });
  const { pushNotification, dismissNotification } = useNotification();
  const detailFormRef = useRef<FormikProps<UpgradePlanPayload>>(null);

  const defaultPhoneCountryCode = useMemo(
    () =>
      phoneCountryCodes.filter((country) => country.name === config.name)[0]
        .label,
    [config.name]
  );

  const initialFormValues: UpgradePlanPayload = {
    street: '',
    zip: '',
    city: 'City',
    industry: DEFAULT_OPTION,
    no_of_employees: EmployeeRangeOptions[0].value,
    reason: DEFAULT_OPTION
  };

  const benefits = [
    { icon: <IcGift /> },
    { icon: <IcVipSupport /> },
    { icon: <IcNoFee /> },
    { icon: <IcAllowance /> },
    { icon: <IcPro /> },
    { icon: <IcOrders /> },
    { icon: <IcProfile /> },
    { icon: <IcPhoneInactive /> }
  ];

  const links = [
    {
      title: LabelText.ALLOWANCE_LINK_TITLE,
      label: 'allowance_link',
      url: `https://www.foodpanda.${countryCode}/corporate/allowance`
    },
    {
      title: LabelText.GC_LINK_TITLE,
      label: 'gc_link',
      url: `https://www.foodpanda.${countryCode}/corporate/giftcards`
    },
    {
      title: LabelText.PANDAPRO_LINK_TITLE,
      label: 'subscription_link',
      url: `https://www.foodpanda.${countryCode}/corporate/subscription`
    }
  ];

  const createUpgradeRequest = useMutation<
    SuccessResponse,
    Error,
    UpgradePlanPayload
  >((payload) => {
    return api.company.upgradePlan.create(payload);
  });

  const { isLoading: isCitiesLoading, refetch } = useQuery(
    ['Cities'],
    () => api.company.location.getCities(),
    {
      onSuccess: (response) => {
        setCities(['City'].concat(response.data.map((city) => city.name)));
      },
      onError: () => {
        pushNotification('Failed to fetch cities', {
          variant: 'error',
          autoHideDuration: 10000
        });
      },
      refetchOnWindowFocus: false,
      enabled: false
    }
  );

  const getSalesforceMappingQuery = useQueryWrapperP1(
    ['SalesforceMapping'],
    () => api.company.upgradePlan.getSalesforceMapping(),
    {
      onSuccess(data) {
        setDropdownOptions({
          industries: [
            DEFAULT_OPTION,
            ...data.data.business_type.map(
              (industry: SalesforceTag) => industry.key
            )
          ] as IndustryKey[],
          reasons: [
            DEFAULT_OPTION,
            ...data.data.reason_for_choosing_us.map(
              (reason: SalesforceTag) => reason.key
            )
          ] as ReasonKey[]
        });
      },
      onError: () => {
        pushNotification('Failed to fetch form dropdown options', {
          variant: 'error',
          autoHideDuration: 10000
        });
      }
    }
  );

  const handleUpgradeRequest = (
    formValues: UpgradePlanPayload,
    actions: FormikHelpers<UpgradePlanPayload>
  ) => {
    const payload = {
      ...formValues,
      no_of_employees: Number(formValues.no_of_employees)
    };

    const createInProgress = pushNotification('Submitting request...');

    createUpgradeRequest.mutate(payload, {
      onSuccess: () => {
        dismissNotification(createInProgress);
        pushNotification('Request submitted!', {
          variant: 'success'
        });
        actions.resetForm();
        setAction(UpgradePlanAction.Request, false);
        queryClient.refetchQueries('user');
      },
      onError: (e) => {
        dismissNotification(createInProgress);
        pushNotification(e.message, {
          variant: 'error',
          onClick: () => detailFormRef.current?.submitForm()
        });
      },
      onSettled: () => {
        dismissNotification(createInProgress);
        actions.setSubmitting(false);
      }
    });
  };

  const handleSendGiftCardClick = () => {
    navigate(
      {
        pathname: AppRoute.GiftCard
      },
      { replace: true }
    );
  };

  const renderBenefits = (icon: JSX.Element, index: number) => {
    return (
      <Typography
        key={`benefit-${index}`}
        type="label-md"
        color="neutral-primary"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {icon}
        {LabelText[`BENEFIT_LINE_${index + 1}`]}
      </Typography>
    );
  };

  const renderLinks = (title: string, label: string, url: string) => {
    return (
      <div key={label} className="upgrade-account-container__links">
        <a aria-label={label} target="_blank" rel="noreferrer" href={url}>
          <Button
            type="button"
            variant="text"
            layout="full-width-secondary"
            prefix={title}
            suffix={<IcDiagonal fill="interaction-primary" cursor="pointer" />}
          >
            {/* to resolve ts error Property 'children' is missing in type */}
            {''}
          </Button>
        </a>
      </div>
    );
  };

  return (
    <Page
      actions={{
        [UpgradePlanAction.Request]: {
          component: (
            <UpgradePlanForm
              defaultPhoneCountryCode={defaultPhoneCountryCode}
              cities={cities}
              dropdownOptions={dropdownOptions}
              detailFormRef={detailFormRef}
              initialValues={initialFormValues}
              isLoading={isCitiesLoading || getSalesforceMappingQuery.isLoading}
              onSubmit={handleUpgradeRequest}
              user={user}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: LabelText.PAGE_TITLE,
            action: {
              label: LabelText.PAGE_ACTION_BUTTON,
              callback: () => detailFormRef.current?.handleSubmit()
            }
          }
        }
      }}
    >
      <Grid rowGap="24px" className="upgrade-account-container">
        <Grid gridTemplateColumns="2fr 1fr" columnGap="24px">
          <div className="upgrade-account-container__banner">
            <div className="upgrade-account-container__banner__content">
              <Typography
                type="title-lg"
                color="brand-primary"
                className="upgrade-account-container__banner__title"
              >
                <FoodpandaForBusiness />
                gift card
              </Typography>
              <Typography
                type="title-sm"
                color="neutral-primary"
                marginTop="sm"
              >
                The fastest and easiest way <br />
                to make someone’s day
              </Typography>
              <Button
                marginTop="sm"
                size="small"
                type="button"
                onClick={handleSendGiftCardClick}
              >
                {LabelText.SEND_BUTTON}
              </Button>
            </div>
            <PauPauGift />
          </div>
          <Card>
            <Grid rowGap="4px">
              <Typography
                type="title-md"
                color="neutral-primary"
                marginBottom="st"
              >
                {LabelText.LINKS_TITLE}
              </Typography>
              {links.map((link) =>
                renderLinks(link.title, link.label, link.url)
              )}
            </Grid>
          </Card>
        </Grid>
        <Card className="upgrade-account-container__information">
          {!user.company.is_review_required ? (
            <>
              <div>
                <Typography
                  type="title-lg"
                  color="neutral-primary"
                  paddingBottom="sm"
                >
                  {LabelText.BENEFITS_TITLE}
                </Typography>
                <Typography
                  type="label-lg"
                  color="neutral-secondary"
                  paddingBottom="sm"
                >
                  {LabelText.BENEFITS_SUBTITLE}
                </Typography>
                <Grid
                  rowGap="8px"
                  className="upgrade-account-container__benefits"
                >
                  {benefits.map((benefit, index) =>
                    renderBenefits(benefit.icon, index)
                  )}
                </Grid>
                <Button
                  marginTop="md"
                  marginBottom="xs"
                  size="regular"
                  type="button"
                  onClick={() => {
                    refetch();
                    setAction(UpgradePlanAction.Request);
                  }}
                >
                  {LabelText.UPGRADE_BUTTON}
                </Button>
                <Typography type="label-sm" color="neutral-secondary">
                  {LabelText.DISCLAIMER_INFO}
                </Typography>
              </div>
              <img
                src="https://images.deliveryhero.io/image/foodpanda/corporate/gift_card_web/paupau_upgrade_request.png"
                height="54%"
              />
            </>
          ) : (
            <>
              <div>
                <Typography
                  type="title-lg"
                  color="neutral-primary"
                  paddingBottom="sm"
                >
                  {LabelText.SUCCESSFUL_REQUEST_TITLE}
                </Typography>
                <Typography type="title-md-secondary" color="neutral-primary">
                  Hold tight while we review your request
                  <br /> You&apos;ll get an email once we&apos;re ready for you
                  to try it.
                  <br />
                  <br />
                  Thank you,
                </Typography>
                <Typography
                  type="title-md"
                  color="neutral-primary"
                  paddingBottom="md"
                >
                  foodpanda for business team
                </Typography>
                <Typography type="label-sm" color="neutral-secondary">
                  {LabelText.DISCLAIMER_INFO}
                </Typography>
              </div>
              <img
                src="https://images.deliveryhero.io/image/foodpanda/corporate/gift_card_web/working_paupau_upgrade.png"
                height="85%"
              />
            </>
          )}
        </Card>
      </Grid>
    </Page>
  );
}
